import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight, Languages } from "lucide-react";
import { ROUTES, CONTACT } from "../data/content";
import { useEnquiry } from "./EnquiryModal";
import { useLanguage } from "../i18n/LanguageContext";

const LangToggle = ({ compact = false }) => {
  const { lang, setLang } = useLanguage();
  return (
    <div
      data-testid="language-toggle"
      className={`flex items-center gap-1 rounded-full border border-amber-500/25 ${compact ? "p-0.5" : "p-1"}`}
    >
      <Languages className="mx-1 h-3.5 w-3.5 text-amber-400/70" />
      {[
        { code: "en", label: "EN" },
        { code: "ar", label: "عربي" },
      ].map((l) => (
        <button
          key={l.code}
          data-testid={`lang-toggle-${l.code}`}
          onClick={() => setLang(l.code)}
          className={`rounded-full px-3 py-1 font-display text-xs font-semibold transition-colors duration-300 ${
            lang === l.code ? "bg-amber-500 text-[#0B1D16]" : "text-slate-400 hover:text-emerald-50"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { open: openEnquiry } = useEnquiry();
  const { t, isAr } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "border-b border-amber-500/15 bg-[#09121F]/85 backdrop-blur-xl"
            : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
          <Link to="/" data-testid="navbar-brand-logo" className="group flex shrink-0 items-center gap-3.5 pe-10">
            <span className="relative">
              <span className="absolute inset-0 rounded-full bg-amber-500/25 blur-lg transition-opacity duration-500 group-hover:opacity-100" />
              <img
                src="/assets/logo-mark.png"
                alt="Al Nama Importer logo"
                className="relative h-14 w-auto object-contain drop-shadow-[0_0_12px_rgba(212,175,55,0.35)]"
              />
            </span>
            <span className="leading-tight">
              <span className="block font-serif text-xl font-bold tracking-wide text-emerald-50 sm:text-2xl">
                {isAr ? (
                  <span className="text-gold-gradient">{CONTACT.companyAr}</span>
                ) : (
                  <>
                    Al Nama <span className="text-gold-gradient">Importer</span>
                  </>
                )}
              </span>
              <span className="block text-[10px] font-medium uppercase tracking-[0.32em] text-amber-400/80">
                {isAr ? "نربط أسواق الغذاء العالمية" : "Connecting Global Food Markets"}
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-5 min-[1380px]:flex">
            {ROUTES.map((r) => (
              <NavLink
                key={r.to}
                to={r.to}
                data-testid={`nav-link-${r.key}`}
                className={({ isActive }) =>
                  `whitespace-nowrap leading-none font-display text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                    isActive ? "text-amber-400" : "text-slate-300 hover:text-emerald-50"
                  }`
                }
              >
                {t(`nav.${r.key}`)}
              </NavLink>
            ))}
            <LangToggle compact />
            <button
              data-testid="navbar-request-enquiry-btn"
              onClick={() => openEnquiry()}
              className="group inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-gradient-to-br from-[#DFB756] to-[#C49A2D] px-5 py-2.5 font-display text-[13px] font-semibold uppercase tracking-wider text-[#0B1D16] transition-transform duration-300 hover:scale-105"
            >
              {t("nav.enquiry")}
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </nav>

          <div className="flex items-center gap-3 min-[1380px]:hidden">
            <LangToggle compact />
            <button
              data-testid="navbar-mobile-menu-btn"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              className="rounded-md border border-amber-500/25 p-2 text-emerald-50"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="navbar-mobile-drawer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex flex-col bg-[#0B1D16]/97 backdrop-blur-xl"
          >
            <div className="flex h-[72px] items-center justify-between px-5">
              <span className="font-serif text-lg font-semibold text-emerald-50">
                {isAr ? CONTACT.companyAr : "Al Nama Importer"}
              </span>
              <button
                data-testid="navbar-mobile-close-btn"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="rounded-md border border-amber-500/25 p-2 text-emerald-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {ROUTES.map((r, i) => (
                <motion.div
                  key={r.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.4 }}
                >
                  <NavLink
                    to={r.to}
                    className={({ isActive }) =>
                      `block border-b border-amber-500/10 py-4 font-serif text-2xl ${
                        isActive ? "text-amber-400" : "text-emerald-50"
                      }`
                    }
                  >
                    {t(`nav.${r.key}`)}
                  </NavLink>
                </motion.div>
              ))}
              <motion.button
                data-testid="navbar-mobile-enquiry-btn"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55 }}
                onClick={() => {
                  setOpen(false);
                  openEnquiry();
                }}
                className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-br from-[#DFB756] to-[#C49A2D] px-6 py-4 font-display text-sm font-semibold uppercase tracking-wider text-[#0B1D16]"
              >
                {t("nav.enquiry")}
                <ArrowUpRight className="h-4 w-4" />
              </motion.button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
